// An achievement badge: a category's emblem from badges.ts, or the Phoenix from phoenix.ts, labelled
// for assistive tech ("Firekeeper, Gold"; "The Phoenix, 3 of 8 categories Ablaze").
import type { BadgeCategory as Category } from '@wbwl/contract'
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react'
import { BADGE_CATEGORY_NAMES, BADGE_TIER_NAMES, badgeSvg, ensureBadgeDefs, type BadgeTier } from './badges.ts'
import { watchBadge } from './badge-motion.ts'
import { phoenixSvg } from './phoenix.ts'

export { BADGE_CATEGORIES, BADGE_CATEGORY_NAMES, BADGE_TIERS, BADGE_TIER_NAMES, type BadgeTier } from './badges.ts'
/** Every category, the Phoenix included. */
export type BadgeCategory = Category

export function badgeLabel(category: Category, tier: BadgeTier, lit?: number): string {
  if (category === 'phoenix') {
    if (tier === 'blaze' || lit === 1) return 'The Phoenix'
    return lit ? `The Phoenix, ${Math.round(lit * 8)} of 8 categories Ablaze` : 'The Phoenix, in its ashes'
  }
  return `${BADGE_CATEGORY_NAMES[category]}, ${BADGE_TIER_NAMES[tier]}`
}

type Props = {
  category: Category
  tier: BadgeTier
  /** Width and height in px. */
  size: number
  /** The small-size drawing (beside a name). Defaults to on at 32px and below. */
  simple?: boolean
  /** The Phoenix only: the share of categories Ablaze (0 to 1). Without it, Ablaze is risen and anything else is ashes. */
  lit?: number
  /** Locked only: drawn in blue, for a blue card where the locked grey would vanish. */
  ghost?: boolean
  /** A tooltip; it also replaces the spoken label. */
  title?: string
  className?: string
}

export function Badge(props: Props) {
  return props.category === 'phoenix' ? <PhoenixBadge {...props} /> : <EmblemBadge {...props} category={props.category} />
}

function EmblemBadge({ category, tier, size, simple = size <= 32, ghost = false, title, className }: Props & { category: Exclude<Category, 'phoenix'> }) {
  // Before the first badge paints, so its gradients are there from the first frame.
  ensureBadgeDefs()
  // Memoised so a parent's re-render keeps the same markup and the shine and embers don't restart.
  const html = useMemo(() => badgeSvg(category, tier, { size, simple, ghost, decorative: true }), [category, tier, size, simple, ghost])
  // Locked, bronze and silver never move.
  const box = useMotion(html, tier === 'gold' || tier === 'blaze', tier === 'blaze')
  return <span ref={box} className={className ? `ach-badge-box ${className}` : 'ach-badge-box'} role="img" aria-label={title ?? badgeLabel(category, tier)} title={title} dangerouslySetInnerHTML={{ __html: html }} />
}

/** A moving badge's box, watched so it holds still out of sight (badge-motion.ts); again whenever its drawing changes. */
function useMotion(html: string, moves: boolean, blaze = false) {
  const box = useRef<HTMLSpanElement>(null)
  useEffect(() => (moves && box.current ? watchBadge(box.current, blaze) : undefined), [html, moves, blaze])
  return box
}

// The page's theme. The Phoenix bakes its night or paper colours into its markup (the emblems
// paint through CSS variables instead), so it redraws when the theme changes.
const watchTheme = (onChange: () => void) => {
  const watcher = new MutationObserver(onChange)
  watcher.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
  return () => watcher.disconnect()
}
const themeNow = () => document.documentElement.dataset.theme ?? ''
const useTheme = () => useSyncExternalStore(watchTheme, themeNow, () => '')

function PhoenixBadge({ tier, size, simple = size <= 32, lit, title, className }: Props) {
  const share = lit ?? (tier === 'blaze' ? 1 : 0)
  const theme = useTheme()
  const html = useMemo(() => phoenixSvg({ size, simple, lit: share, decorative: true }), [size, simple, share, theme])
  const box = useMotion(html, true)
  return <span ref={box} className={className ? `ach-badge-box ${className}` : 'ach-badge-box'} role="img" aria-label={title ?? badgeLabel('phoenix', tier, share)} title={title} dangerouslySetInnerHTML={{ __html: html }} />
}

/** The badge a member shows beside their name (Silver and up, or the risen Phoenix), or nothing: guests and the rest show none. */
export function NameBadge({ badge, size = 20 }: { badge: { category: Category; tier: BadgeTier } | null | undefined; size?: number }) {
  if (!badge) return null
  return <Badge category={badge.category} tier={badge.tier} size={size} className="name-badge" title={badgeLabel(badge.category, badge.tier)} />
}
